/** @format */

import { Condition } from './workflow.models';

/**
 * Evaluates a list of conditions (AND/OR-joined) against current field values. Used for
 * field-level conditional visibility in the workflow runtime form.
 *
 * Mirrors the api's WorkflowRoutingEngine.EvaluateConditionalRoute / EvaluateSingleCondition
 * exactly, so a condition means the same thing everywhere it's evaluated: an unknown operator or
 * an empty condition list both resolve to true (unconditional); a genuinely unset source value
 * fails equals/gt/lt (there is nothing to compare it against).
 */
export function evaluateConditions(conditions: Condition[], values: Record<string, any>): boolean {
  const usable = (conditions || []).filter((c) => c.fieldId);
  if (!usable.length) return true;

  return usable.reduce((acc: boolean, condition, i) => {
    const met = evaluateSingle(condition, values);
    if (i === 0) return met;
    return condition.join === 'OR' ? acc || met : acc && met;
  }, false);
}

function evaluateSingle(condition: Condition, values: Record<string, any>): boolean {
  const actual = toComparable(values[condition.fieldId]);
  const expected = (condition.value ?? '').toString().trim();

  switch (condition.op) {
    case 'equals':
      return actual.toLowerCase() === expected.toLowerCase();
    case 'not_equals':
      return actual.toLowerCase() !== expected.toLowerCase();
    case 'contains':
      return actual.toLowerCase().includes(expected.toLowerCase());
    case 'gt': {
      const a = parseNumber(actual);
      const b = parseNumber(expected);
      return a !== null && b !== null && a > b;
    }
    case 'lt': {
      const a = parseNumber(actual);
      const b = parseNumber(expected);
      return a !== null && b !== null && a < b;
    }
    default:
      return true;
  }
}

function toComparable(value: any): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.join(',');
  return value.toString().trim();
}

/** '' is "no value", not zero — must fail a numeric comparison, matching decimal.TryParse. */
function parseNumber(s: string): number | null {
  if (s === '') return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}
